import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { CHART_INK, formatMonthLabel } from './chartUtils';

// Generic monthly count trend (area chart). `monthlyData` items must have
// `{year, month, count}` — any monthly-stats endpoint can feed this directly.
const TrendAreaChart = ({
    monthlyData,
    expanded,
    color,
    totalLabel = 'סה״כ בטווח שנבחר',
    unitLabel = 'רשומות',
    gradientId = 'trendAreaGradient',
}) => {
    const data = useMemo(
        () => (monthlyData || []).map((m) => ({ label: formatMonthLabel(m.year, m.month), count: m.count })),
        [monthlyData],
    );

    const total = useMemo(() => data.reduce((sum, m) => sum + m.count, 0), [data]);
    const height = expanded ? 300 : 190;

    if (!data.length) {
        return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>אין נתונים לטווח שנבחר</Typography>;
    }

    return (
        <Box>
            <Typography variant="h4" fontWeight={700} color={color}>
                {total.toLocaleString('he-IL')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                {totalLabel}
            </Typography>
            <ResponsiveContainer width="100%" height={height}>
                <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={{ stroke: CHART_INK.axis }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
                    <Tooltip formatter={(value) => [`${value} ${unitLabel}`, '']} cursor={{ stroke: CHART_INK.axis, strokeWidth: 1 }} />
                    <Area
                        type="monotone"
                        dataKey="count"
                        name={unitLabel}
                        stroke={color}
                        strokeWidth={2}
                        fill={`url(#${gradientId})`}
                        dot={{ r: 3, fill: color, strokeWidth: 0 }}
                        activeDot={{ r: 5 }}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default TrendAreaChart;
